/**
 * Plantilla y validación del Excel de Asignación de Turnos.
 *
 * Sin React ni antd a propósito: así el workbook y el validador se pueden generar
 * y revisar con un `node script.js`, sin levantar la app.
 *
 * La hoja principal se descarga YA POBLADA con las asignaciones actuales (con su
 * ID y su Estado): el flujo es descargar → editar → subir, sin copiar y pegar.
 *
 * Lo que el Excel puede hacer: crear asignaciones nuevas y habilitar/deshabilitar
 * las que ya existen. Lo que NO: cambiarle el empleado, el turno o las fechas a una
 * asignación existente. Ojo con esto último: el modelo tiene un índice único
 * {turnoId, empleadoId}, así que tampoco se puede crear un segundo registro del
 * mismo par para otro rango de fechas — de ahí que cambiar fechas tenga que
 * hacerse en la pantalla.
 *
 * A diferencia de las asignaciones de reloj, un turno NO se manda al dispositivo:
 * los horarios los resuelve el servidor al armar los reportes de asistencia.
 */

import * as XLSX from "xlsx";
import { encodeId, decodeId } from "../../../../Components/idEncoder";
import {
  ESTADO_NOMBRE,
  claveSerieNumero,
  construirIndiceEmpleados,
  construirIndiceEtiquetado,
  formatearFechaDia,
  hojaDeCatalogo,
  hojaDeFilas,
  hojaDeInstrucciones,
  nombreEmpleado,
  parsearEstado,
  parsearFechaExcel,
  resolverEmpleado,
  resolverEtiquetado,
  texto,
} from "../../../../Components/importarAsignaciones.utils";

export const COLUMNAS = [
  "ID",
  "Número Empleado",
  "Empleado",
  "Turno",
  "Inicio",
  "Fin",
  "Estado",
];

const ANCHOS = [16, 16, 34, 34, 14, 14, 16];

export const NOMBRE_PLANTILLA = "plantillaAsignacionTurnos.xlsx";

// Misma forma que la columna "Turno" de la tabla de Asignación de Turnos.
export const etiquetaTurno = (turno) => {
  if (!turno) return "";
  return [claveSerieNumero(turno), texto(turno.nombre)].filter(Boolean).join(" - ");
};

// Las fechas guardadas se comparan y se pintan como "YYYY-MM-DD" en hora local del
// navegador, que es el mismo día que muestra la tabla.
const claveDia = (valor) => {
  const iso = parsearFechaExcel(formatearFechaDia(valor));
  return iso || "";
};

// Dos rangos se traslapan si cada uno empieza antes de que el otro termine. Con
// fechas "YYYY-MM-DD" la comparación de cadenas es la comparación de fechas.
const seTraslapan = (aInicio, aFin, bInicio, bFin) => aInicio <= bFin && aFin >= bInicio;

export const prepararCatalogos = (resData) => {
  const asignaciones = resData.asignaciones || [];
  const empleados = resData.empleados || [];
  const turnos = resData.turnos || [];

  const porEmpleado = new Map();
  for (const asignacion of asignaciones) {
    const clave = String(asignacion.empleadoId);
    if (!porEmpleado.has(clave)) porEmpleado.set(clave, []);
    porEmpleado.get(clave).push(asignacion);
  }

  return {
    asignaciones,
    empleados,
    turnos,
    porEmpleado,
    asignacionPorId: new Map(asignaciones.map((a) => [String(a._id), a])),
    empleadoPorId: new Map(empleados.map((e) => [String(e._id), e])),
    turnoPorId: new Map(turnos.map((t) => [String(t._id), t])),
    indiceEmpleados: construirIndiceEmpleados(empleados),
    indiceTurnos: construirIndiceEtiquetado(turnos, etiquetaTurno),
  };
};

// ─── Plantilla ───────────────────────────────────────────────────────────────

const INSTRUCCIONES_HOJA = [
  "IMPORTAR / ACTUALIZAR ASIGNACIONES DE TURNOS",
  "",
  'La hoja "Asignaciones" ya viene con todas tus asignaciones actuales. Editala y vuelve a subir este mismo archivo.',
  "",
  "PARA DESHABILITAR: cambia la columna Estado de esa fila a Deshabilitado.",
  "PARA HABILITAR: cambia la columna Estado a Activo.",
  "",
  "PARA CREAR UNA ASIGNACION NUEVA: agrega una fila al final, DEJA LA COLUMNA ID VACIA y llena",
  "   Numero Empleado, Turno, Inicio, Fin y Estado = Activo.",
  "   Las fechas van en formato DD/MM/AAAA.",
  "",
  "PARA CAMBIARLE EL TURNO A UN EMPLEADO: pon Deshabilitado en la fila del turno viejo y agrega",
  "   una fila nueva (sin ID) con el turno nuevo. Se puede hacer en la misma subida.",
  "",
  "LO QUE NO SE PUEDE HACER DESDE AQUI: cambiarle el empleado, el turno o las fechas a una",
  "   asignacion que ya existe (una fila que ya trae ID). Eso se hace en la pantalla de",
  "   Asignacion de Turnos. Si lo intentas, la fila sale reportada en errores.xlsx.",
  "   La razon es que el sistema solo admite UN registro por empleado y turno, asi que no se",
  "   puede crear un segundo con otras fechas.",
  "",
  "Un empleado no puede tener dos asignaciones activas cuyas fechas se traslapen.",
  "",
  "Borrar una fila del archivo NO borra nada: las filas que no vengan se quedan como estan.",
  "",
  'Las hojas "Empleados" y "Turnos" son de consulta: te dicen que valores son validos.',
];

export const construirPlantilla = (catalogos) => {
  const { asignaciones = [], empleados = [], turnos = [] } = catalogos || {};
  const empleadoPorId = catalogos?.empleadoPorId || new Map();
  const turnoPorId = catalogos?.turnoPorId || new Map();

  const filas = asignaciones
    .map((asignacion) => {
      const empleado = empleadoPorId.get(String(asignacion.empleadoId));
      const turno = turnoPorId.get(String(asignacion.turnoId));
      return {
        ID: encodeId(String(asignacion._id)),
        "Número Empleado": claveSerieNumero(empleado),
        Empleado: nombreEmpleado(empleado),
        Turno: etiquetaTurno(turno),
        Inicio: formatearFechaDia(asignacion.inicio),
        Fin: formatearFechaDia(asignacion.fin),
        Estado: ESTADO_NOMBRE[Number(asignacion.estado)] || "",
      };
    })
    .sort((a, b) => a.Empleado.localeCompare(b.Empleado) || a.Turno.localeCompare(b.Turno));

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, hojaDeFilas(COLUMNAS, filas, ANCHOS), "Asignaciones");

  XLSX.utils.book_append_sheet(
    wb,
    hojaDeCatalogo(
      ["Número", "Nombre", "Estado"],
      empleados
        .map((empleado) => ({
          Número: claveSerieNumero(empleado),
          Nombre: nombreEmpleado(empleado),
          Estado: ESTADO_NOMBRE[Number(empleado.estado)] || "",
        }))
        .sort((a, b) => a.Nombre.localeCompare(b.Nombre)),
      [16, 40, 16],
    ),
    "Empleados",
  );

  XLSX.utils.book_append_sheet(
    wb,
    hojaDeCatalogo(
      ["Clave", "Nombre", "Descripción", "Horas", "Estado"],
      turnos.map((turno) => ({
        Clave: claveSerieNumero(turno),
        Nombre: texto(turno.nombre),
        Descripción: texto(turno.descripcion),
        Horas:
          turno.horasTurno === undefined || turno.horasTurno === null
            ? ""
            : String(turno.horasTurno),
        Estado: ESTADO_NOMBRE[Number(turno.estado)] || "",
      })),
      [14, 28, 32, 10, 16],
    ),
    "Turnos",
  );

  XLSX.utils.book_append_sheet(wb, hojaDeInstrucciones(INSTRUCCIONES_HOJA), "Instrucciones");

  return wb;
};

// ─── Validación ──────────────────────────────────────────────────────────────

const numeroDeFila = (fila) => (fila["__rowNum__"] ?? 0) + 1;

const apartados = (contexto, nombre) => {
  if (!contexto[nombre]) contexto[nombre] = new Map();
  return contexto[nombre];
};

/**
 * Pasada previa sobre la hoja, antes de validar renglón por renglón.
 *
 * Junta los ids de las asignaciones que el propio archivo va a deshabilitar. Sin
 * esto, el caso normal de "cambiarle el turno a un empleado" (apagar el viejo +
 * agregar el nuevo en la misma subida) se rechazaría por traslape contra la
 * asignación que la subida está por apagar.
 */
export const prepararContextoArchivo = (hojas, catalogos) => {
  const idsDesactivados = new Set();

  for (const fila of hojas?.[0]?.data || []) {
    const idEncoded = texto(fila["ID"]);
    if (!idEncoded) continue;

    const decodificado = decodeId(idEncoded);
    const asignacion = decodificado
      ? catalogos.asignacionPorId.get(String(decodificado))
      : null;

    if (!asignacion) continue;
    if (Number(asignacion.estado) !== 1) continue;
    if (parsearEstado(fila["Estado"]) === 2) idsDesactivados.add(String(asignacion._id));
  }

  return { idsDesactivados };
};

export const validarFila = (fila, catalogos, _indice, contexto) => {
  const idEncoded = texto(fila["ID"]);
  const numeroTexto = texto(fila["Número Empleado"]);
  const nombreTexto = texto(fila["Empleado"]);
  const turnoTexto = texto(fila["Turno"]);
  const estadoTexto = texto(fila["Estado"]);

  const errores = [];

  const estadoDestino = parsearEstado(estadoTexto);
  if (!estadoTexto) {
    errores.push({ campo: "Estado", motivo: 'está vacío (pon "Activo" o "Deshabilitado")' });
  } else if (!estadoDestino) {
    errores.push({
      campo: "Estado",
      motivo: `"${estadoTexto}" no se entiende; usa "Activo" o "Deshabilitado"`,
    });
  }

  const resEmpleado = resolverEmpleado(catalogos.indiceEmpleados, { numeroTexto, nombreTexto });
  if (resEmpleado.errores) errores.push(...resEmpleado.errores);

  const resTurno = resolverEtiquetado(catalogos.indiceTurnos, turnoTexto, {
    campo: "Turno",
    hoja: "Turnos",
    ejemplo: "TUR - 1",
  });
  if (resTurno.errores) errores.push(...resTurno.errores);

  const inicio = parsearFechaExcel(fila["Inicio"]);
  const fin = parsearFechaExcel(fila["Fin"]);

  if (!inicio) {
    errores.push({
      campo: "Inicio",
      motivo: texto(fila["Inicio"])
        ? `"${texto(fila["Inicio"])}" no es una fecha (usa DD/MM/AAAA)`
        : "está vacío",
    });
  }
  if (!fin) {
    errores.push({
      campo: "Fin",
      motivo: texto(fila["Fin"])
        ? `"${texto(fila["Fin"])}" no es una fecha (usa DD/MM/AAAA)`
        : "está vacío",
    });
  }
  if (inicio && fin && inicio > fin) {
    errores.push({ campo: "Fin", motivo: "es anterior a la fecha de inicio" });
  }

  if (errores.length > 0) return { ok: false, errores };

  const empleado = resEmpleado.empleado;
  const turno = resTurno.item;

  const datos = {
    empleadoId: String(empleado._id),
    turnoId: String(turno._id),
    inicio,
    fin,
    // Sólo para la vista previa (el backend los ignora).
    empleadoNumero: claveSerieNumero(empleado),
    empleadoNombre: nombreEmpleado(empleado),
    turnoNombre: etiquetaTurno(turno),
    inicioTexto: formatearFechaDia(inicio),
    finTexto: formatearFechaDia(fin),
    estadoNombre: ESTADO_NOMBRE[estadoDestino],
  };

  // ── Fila con ID: sólo puede prender o apagar ────────────────────────────────
  if (idEncoded) {
    const decodificado = decodeId(idEncoded);
    const asignacion = decodificado
      ? catalogos.asignacionPorId.get(String(decodificado))
      : null;

    if (!asignacion) {
      return {
        ok: false,
        errores: [{ campo: "ID", motivo: `"${idEncoded}" no corresponde a ninguna asignación` }],
      };
    }

    const idsDelArchivo = apartados(contexto, "idsUsados");
    if (idsDelArchivo.has(idEncoded)) {
      return {
        ok: false,
        errores: [
          { campo: "ID", motivo: `repetido (también está en la fila ${idsDelArchivo.get(idEncoded)})` },
        ],
      };
    }
    idsDelArchivo.set(idEncoded, numeroDeFila(fila));

    const noEditables = [];
    if (String(asignacion.empleadoId) !== String(empleado._id)) noEditables.push("Empleado");
    if (String(asignacion.turnoId) !== String(turno._id)) noEditables.push("Turno");
    if (claveDia(asignacion.inicio) !== inicio) noEditables.push("Inicio");
    if (claveDia(asignacion.fin) !== fin) noEditables.push("Fin");

    if (noEditables.length > 0) {
      return {
        ok: false,
        errores: noEditables.map((campo) => ({
          campo,
          motivo:
            "desde el Excel sólo se puede habilitar o deshabilitar; este cambio se hace en la pantalla de Asignación de Turnos",
        })),
      };
    }

    const estadoActual = Number(asignacion.estado);
    const valoresAnteriores = { estadoNombre: ESTADO_NOMBRE[estadoActual] || "" };

    if (estadoActual === estadoDestino) {
      return {
        ok: true,
        accion: "sinCambios",
        idExistente: String(asignacion._id),
        datos: { ...datos, operacion: "sinCambios" },
        valoresAnteriores,
      };
    }

    return {
      ok: true,
      accion: "editar",
      idExistente: String(asignacion._id),
      datos: { ...datos, operacion: estadoDestino === 1 ? "habilitar" : "deshabilitar" },
      cambios: ["estadoNombre"],
      valoresAnteriores,
    };
  }

  // ── Fila sin ID: alta ───────────────────────────────────────────────────────
  if (estadoDestino === 2) {
    return {
      ok: false,
      errores: [
        {
          campo: "Estado",
          motivo:
            'una fila nueva no se puede crear deshabilitada: déjala en "Activo" o quítala del archivo',
        },
      ],
    };
  }

  if (Number(empleado.estado) !== 1) {
    return {
      ok: false,
      errores: [{ campo: "Empleado", motivo: `"${nombreEmpleado(empleado)}" está dado de baja` }],
    };
  }

  if (Number(turno.estado) !== 1) {
    return {
      ok: false,
      errores: [{ campo: "Turno", motivo: `"${etiquetaTurno(turno)}" está deshabilitado` }],
    };
  }

  const delEmpleado = catalogos.porEmpleado.get(String(empleado._id)) || [];

  // El índice único {turnoId, empleadoId} impide un segundo registro del mismo
  // par, así que si ya existe no hay alta posible: o coincide en fechas (y
  // entonces es prender/nada) o hay que ir a la pantalla.
  const mismoPar = delEmpleado.find((a) => String(a.turnoId) === String(turno._id));

  if (mismoPar) {
    const mismasFechas = claveDia(mismoPar.inicio) === inicio && claveDia(mismoPar.fin) === fin;

    if (!mismasFechas) {
      return {
        ok: false,
        errores: [
          {
            campo: "Turno",
            motivo: `el empleado ya tiene ese turno asignado del ${formatearFechaDia(mismoPar.inicio)} al ${formatearFechaDia(mismoPar.fin)}; el sistema sólo admite un registro por empleado y turno, así que las fechas se cambian en la pantalla`,
          },
        ],
      };
    }

    if (Number(mismoPar.estado) === 1) {
      return {
        ok: true,
        accion: "sinCambios",
        idExistente: String(mismoPar._id),
        datos: { ...datos, operacion: "sinCambios" },
        valoresAnteriores: { estadoNombre: "Activo" },
      };
    }

    return {
      ok: true,
      accion: "editar",
      idExistente: String(mismoPar._id),
      datos: { ...datos, operacion: "habilitar" },
      cambios: ["estadoNombre"],
      valoresAnteriores: { estadoNombre: ESTADO_NOMBRE[Number(mismoPar.estado)] || "" },
    };
  }

  // Traslape contra las otras asignaciones activas del empleado, ignorando las que
  // este mismo archivo va a apagar (ver prepararContextoArchivo).
  const idsDesactivados = contexto.idsDesactivados || new Set();

  const traslape = delEmpleado.find(
    (a) =>
      Number(a.estado) === 1 &&
      !idsDesactivados.has(String(a._id)) &&
      seTraslapan(inicio, fin, claveDia(a.inicio), claveDia(a.fin)),
  );

  if (traslape) {
    const turnoEnConflicto = catalogos.turnoPorId.get(String(traslape.turnoId));
    return {
      ok: false,
      errores: [
        {
          campo: "Inicio",
          motivo: `se traslapa con "${etiquetaTurno(turnoEnConflicto)}" (${formatearFechaDia(traslape.inicio)} al ${formatearFechaDia(traslape.fin)}); deshabilita esa fila en este mismo archivo o cambia las fechas`,
        },
      ],
    };
  }

  // Traslape contra las otras filas nuevas del mismo archivo.
  const nuevasDelArchivo = apartados(contexto, "nuevasPorEmpleado");
  const claveEmpleado = String(empleado._id);
  const previas = nuevasDelArchivo.get(claveEmpleado) || [];

  const choque = previas.find((previa) => seTraslapan(inicio, fin, previa.inicio, previa.fin));
  if (choque) {
    return {
      ok: false,
      errores: [
        {
          campo: "Inicio",
          motivo: `se traslapa con la fila ${choque.numeroFila} de este mismo archivo`,
        },
      ],
    };
  }

  nuevasDelArchivo.set(claveEmpleado, [
    ...previas,
    { inicio, fin, numeroFila: numeroDeFila(fila) },
  ]);

  return { ok: true, accion: "crear", datos: { ...datos, operacion: "crear" } };
};
